import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, UserCheck, Download, MoreVertical } from 'lucide-react';
import { Enquiry } from '../../types/api';
import { enquiryAPI, membershipPlanAPI } from '../../services/api';
import SearchBar from '../common/SearchBar';
import Pagination from '../common/Pagination';
import { ToastContainer } from '../common/Toast';
import { useToast } from '../../hooks/useToast';
import { useAuth } from '../../contexts/AuthContext';

const Enquiries: React.FC = () => {
  const { user } = useAuth();
  const [openEnquiries, setOpenEnquiries] = useState<Enquiry[]>([]);
  const [closedEnquiries, setClosedEnquiries] = useState<Enquiry[]>([]);
  const [filteredOpenEnquiries, setFilteredOpenEnquiries] = useState<Enquiry[]>([]);
  const [filteredClosedEnquiries, setFilteredClosedEnquiries] = useState<Enquiry[]>([]);
  const [paginatedEnquiries, setPaginatedEnquiries] = useState<Enquiry[]>([]);
  const [membershipPlans, setMembershipPlans] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [selectedEnquiry, setSelectedEnquiry] = useState<Enquiry | null>(null);
  const [formData, setFormData] = useState<Partial<Enquiry>>({});
  const [activeTab, setActiveTab] = useState<'open' | 'closed'>('open');
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  
  // Confirmation dialog state
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmDialogMessage, setConfirmDialogMessage] = useState('');
  const [confirmDialogAction, setConfirmDialogAction] = useState<(() => void) | null>(null);
  
  // Search and Pagination state
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(25);
  const [openDropdown, setOpenDropdown] = useState<number | null>(null);

  // Toast notifications
  const { toasts, removeToast, success, error, warning, info } = useToast();
  
  // Confirmation dialog helper
  const showConfirmation = (message: string, onConfirm: () => void) => {
    setConfirmDialogMessage(message);
    setConfirmDialogAction(() => onConfirm);
    setShowConfirmDialog(true);
  };
  
  const handleConfirmAction = () => {
    if (confirmDialogAction) {
      confirmDialogAction();
    }
    setShowConfirmDialog(false);
    setConfirmDialogAction(null);
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setOpenDropdown(null);
    };
    if (openDropdown !== null) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [openDropdown]);

  // Apply search filter
  useEffect(() => {
    applySearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openEnquiries, closedEnquiries, searchQuery, activeTab]);

  // Apply pagination
  useEffect(() => {
    applyPagination();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredOpenEnquiries, filteredClosedEnquiries, activeTab, currentPage, itemsPerPage]);

  const fetchData = async () => {
    try {
      const [openData, closedData, plansData] = await Promise.all([
        enquiryAPI.getOpen(),
        enquiryAPI.getClosed(),
        membershipPlanAPI.getActive()
      ]);
      setOpenEnquiries(openData);
      setClosedEnquiries(closedData);
      setMembershipPlans(plansData);
    } catch (err) {
      error('Failed to fetch enquiries data');
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const applySearch = () => {
    const filterEnquiries = (enquiries: Enquiry[]) => {
      if (!searchQuery) return enquiries;
      
      const query = searchQuery.toLowerCase();
      return enquiries.filter(e =>
        `${e.firstName} ${e.lastName}`.toLowerCase().includes(query) ||
        e.email?.toLowerCase().includes(query) ||
        e.phone?.toLowerCase().includes(query) ||
        e.address?.toLowerCase().includes(query) ||
        e.interestType?.toLowerCase().includes(query)
      );
    };

    setFilteredOpenEnquiries(filterEnquiries(openEnquiries));
    setFilteredClosedEnquiries(filterEnquiries(closedEnquiries));
    setCurrentPage(1); // Reset to first page when search changes
  };

  const applyPagination = () => {
    const currentData = activeTab === 'open' ? filteredOpenEnquiries : filteredClosedEnquiries;
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    setPaginatedEnquiries(currentData.slice(startIndex, endIndex));
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (items: number) => {
    setItemsPerPage(items);
    setCurrentPage(1);
  };

  const getCurrentData = () => activeTab === 'open' ? filteredOpenEnquiries : filteredClosedEnquiries;
  const totalPages = Math.ceil(getCurrentData().length / itemsPerPage);

  const validateForm = (): boolean => {
    const newErrors: {[key: string]: string} = {};

    // Required fields - First Name
    if (!formData.firstName?.trim()) {
      newErrors.firstName = 'First name is required';
    } else if (formData.firstName.length < 2) {
      newErrors.firstName = 'First name must be at least 2 characters';
    } else if (formData.firstName.length > 50) {
      newErrors.firstName = 'First name must not exceed 50 characters';
    }

    // Required fields - Last Name
    if (!formData.lastName?.trim()) {
      newErrors.lastName = 'Last name is required';
    } else if (formData.lastName.length < 2) {
      newErrors.lastName = 'Last name must be at least 2 characters';
    } else if (formData.lastName.length > 50) {
      newErrors.lastName = 'Last name must not exceed 50 characters';
    }

    // Required fields - Email
    if (!formData.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    // Required fields - Phone
    if (!formData.phone?.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (formData.phone.replace(/\D/g, '').length < 10) {
      newErrors.phone = 'Phone number must be at least 10 digits';
    }

    // Required fields - City
    if (!formData.city?.trim()) {
      newErrors.city = 'City is required';
    } else if (formData.city.length < 2) {
      newErrors.city = 'City must be at least 2 characters';
    }

    // Required fields - Gender
    if (!formData.gender?.trim()) {
      newErrors.gender = 'Gender is required';
    }

    // Required fields - Date of Birth
    if (!formData.dateOfBirth) {
      newErrors.dateOfBirth = 'Date of birth is required';
    } else {
      const age = new Date().getFullYear() - new Date(formData.dateOfBirth).getFullYear();
      if (age < 10) {
        newErrors.dateOfBirth = 'Age must be at least 10 years';
      } else if (age > 100) {
        newErrors.dateOfBirth = 'Invalid date of birth';
      }
    }

    // Optional field validations
    if (formData.remark && formData.remark.length > 1000) {
      newErrors.remark = 'Remark must not exceed 1000 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCreate = () => {
    setFormData({});
    setErrors({});
    setShowModal(true);
  };

  const handleEdit = (enquiry: Enquiry) => {
    setFormData(enquiry);
    setErrors({});
    setShowModal(true);
  };

  const handleConvert = (enquiry: Enquiry) => {
    setSelectedEnquiry(enquiry);
    setShowConvertModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      error('Please fix the errors in the form');
      return;
    }

    try {
      if (formData.enquiryId) {
        await enquiryAPI.update(formData.enquiryId, formData);
        success('Enquiry updated successfully!');
      } else {
        // GymId is automatically set by backend from logged-in user's context
        // Don't send gymId from frontend - backend will set it securely from authenticated user
        const { gymId, ...dataToCreate } = formData; // Remove gymId if present
        await enquiryAPI.create(dataToCreate);
        success('Enquiry created successfully!');
      }
      setShowModal(false);
      fetchData();
    } catch (err: any) {
      if (err.response?.data?.message) {
        error(err.response.data.message);
      } else {
        error('Failed to save enquiry');
      }
      console.error('Error saving enquiry:', err);
    }
  };

  const handleDelete = async (id: number) => {
    showConfirmation(
      'Are you sure you want to delete this enquiry?',
      async () => {
        try {
          await enquiryAPI.delete(id);
          success('Enquiry deleted successfully!');
          fetchData();
        } catch (err: any) {
          if (err.response?.data?.message) {
            error(err.response.data.message);
          } else {
            error('Failed to delete enquiry');
          }
          console.error('Error deleting enquiry:', err);
        }
      }
    );
  };

  const handleConvertToMember = async (
    membershipPlanId: number, 
    paidAmount: number,
    discount: number,
    startDate: string,
    paymentMode: string,
    transactionId: string,
    expectedPaymentDate: string
  ) => {
    if (!selectedEnquiry) return;
    
    if (paidAmount < 0) {
      error('Paid amount cannot be negative');
      return;
    }

    if (discount < 0) {
      error('Discount cannot be negative');
      return;
    }

    try {
      await enquiryAPI.convertToMember(
        selectedEnquiry.enquiryId, 
        membershipPlanId, 
        paidAmount,
        discount,
        startDate,
        paymentMode,
        transactionId,
        expectedPaymentDate,
        user?.email || 'admin@gym.com'
      );
      setShowConvertModal(false);
      fetchData();
      success('🎉 Enquiry successfully converted to member! Welcome email sent.');
    } catch (err: any) {
      if (err.response?.data?.message) {
        error(err.response.data.message);
      } else {
        error('Failed to convert enquiry to member');
      }
      console.error('Error converting to member:', err);
    }
  };

  const handleExportToExcel = async () => {
    try {
      info('Exporting enquiries to Excel...');
      const response = await enquiryAPI.exportToExcel();
      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Enquiries_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      success('Enquiries exported successfully!');
    } catch (err) {
      error('Failed to export enquiries to Excel');
      console.error('Error exporting to Excel:', err);
    }
  };

  if (loading) {
    return <div className="loading">Loading enquiries...</div>;
  }

  return (
    <div>
      {/* Toast Container */}
      <ToastContainer toasts={toasts} removeToast={removeToast} />

      <div className="header">
        <h1 className="header-title">Enquiries</h1>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button className="btn btn-success" onClick={handleExportToExcel}>
            <Download size={20} style={{ marginRight: '0.5rem' }} />
            Export to Excel
          </button>
          <button className="btn btn-primary" onClick={handleCreate}>
            <Plus size={20} style={{ marginRight: '0.5rem' }} />
            Add Enquiry
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ marginBottom: '1rem', borderBottom: '2px solid #e0e0e0' }}>
        <div style={{ display: 'flex', gap: '1rem' }}>
          <button
            onClick={() => {
              setActiveTab('open');
              setCurrentPage(1);
            }}
            style={{
              padding: '0.75rem 1.5rem',
              background: activeTab === 'open' ? '#007bff' : 'transparent',
              color: activeTab === 'open' ? 'white' : '#666',
              border: 'none',
              borderBottom: activeTab === 'open' ? '3px solid #007bff' : 'none',
              cursor: 'pointer',
              fontWeight: activeTab === 'open' ? 'bold' : 'normal',
              fontSize: '1rem'
            }}
          >
            Open Enquiries ({filteredOpenEnquiries.length}/{openEnquiries.length})
          </button>
          <button
            onClick={() => {
              setActiveTab('closed');
              setCurrentPage(1);
            }}
            style={{
              padding: '0.75rem 1.5rem',
              background: activeTab === 'closed' ? '#28a745' : 'transparent',
              color: activeTab === 'closed' ? 'white' : '#666',
              border: 'none',
              borderBottom: activeTab === 'closed' ? '3px solid #28a745' : 'none',
              cursor: 'pointer',
              fontWeight: activeTab === 'closed' ? 'bold' : 'normal',
              fontSize: '1rem'
            }}
          >
            Closed Enquiries ({filteredClosedEnquiries.length}/{closedEnquiries.length})
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="card" style={{ marginBottom: '1rem' }}>
        <SearchBar
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="Search by name, email, phone, address, or interest..."
        />
      </div>

      {/* Enquiries Table */}
      <div className="card">
        <div className="card-body">
          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Interest</th>
                  <th>City</th>
                  <th>Created Date</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedEnquiries.length === 0 ? (
                  <tr>
                    <td colSpan={9} style={{ textAlign: 'center', padding: '2rem' }}>
                      No enquiries found
                    </td>
                  </tr>
                ) : (
                  paginatedEnquiries.map((enquiry) => (
                    <tr key={enquiry.enquiryId}>
                      <td>{enquiry.enquiryId}</td>
                      <td>{`${enquiry.firstName} ${enquiry.lastName}`}</td>
                      <td>{enquiry.email}</td>
                      <td>{enquiry.phone}</td>
                      <td>
                        {enquiry.interestType ? (
                          <span className="badge badge-info">{enquiry.interestType}</span>
                        ) : (
                          <span style={{ color: '#999' }}>-</span>
                        )}
                      </td>
                      <td>{enquiry.city || '-'}</td>
                      <td>
                        {enquiry.createdAt 
                          ? new Date(enquiry.createdAt).toLocaleDateString()
                          : 'N/A'}
                      </td>
                      <td>
                        {enquiry.isConverted ? (
                          <span className="badge badge-success">Converted</span>
                        ) : (
                          <span className="badge badge-warning">Open</span>
                        )}
                      </td>
                      <td>
                        <div style={{ position: 'relative' }}>
                          <button 
                            className="btn btn-secondary"
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenDropdown(openDropdown === enquiry.enquiryId ? null : enquiry.enquiryId);
                            }}
                            style={{ padding: '0.25rem 0.5rem' }}
                            title="Actions"
                          >
                            <MoreVertical size={16} />
                          </button>
                          
                          {openDropdown === enquiry.enquiryId && (
                            <div 
                              style={{
                                position: 'absolute',
                                right: 0,
                                top: '100%',
                                marginTop: '0.25rem',
                                backgroundColor: 'white',
                                border: '1px solid #E5E7EB',
                                borderRadius: '6px',
                                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                                zIndex: 1000,
                                minWidth: '180px',
                                overflow: 'hidden'
                              }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {!enquiry.isConverted && (
                                <>
                                  <button
                                    className="btn btn-secondary"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleEdit(enquiry);
                                      setOpenDropdown(null);
                                    }}
                                    style={{
                                      width: '100%',
                                      padding: '0.5rem 1rem',
                                      textAlign: 'left',
                                      border: 'none',
                                      background: 'transparent',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '0.5rem',
                                      cursor: 'pointer'
                                    }}
                                  >
                                    <Edit size={16} />
                                    Edit Enquiry
                                  </button>
                                  <button
                                    className="btn btn-success"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleConvert(enquiry);
                                      setOpenDropdown(null);
                                    }}
                                    style={{
                                      width: '100%',
                                      padding: '0.5rem 1rem',
                                      textAlign: 'left',
                                      border: 'none',
                                      background: 'transparent',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '0.5rem',
                                      cursor: 'pointer',
                                      color: '#059669'
                                    }}
                                  >
                                    <UserCheck size={16} />
                                    Convert to Member
                                  </button>
                                </>
                              )}
                              <button
                                className="btn btn-danger"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDelete(enquiry.enquiryId);
                                  setOpenDropdown(null);
                                }}
                                style={{
                                  width: '100%',
                                  padding: '0.5rem 1rem',
                                  textAlign: 'left',
                                  border: 'none',
                                  background: 'transparent',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.5rem',
                                  cursor: 'pointer',
                                  color: '#DC2626'
                                }}
                              >
                                <Trash2 size={16} />
                                Delete Enquiry
                              </button>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          {getCurrentData().length > 0 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={getCurrentData().length}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
            />
          )}
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px' }}>
            <div className="modal-header">
              <h2 className="modal-title">
                {formData.enquiryId ? 'Edit Enquiry' : 'Add Enquiry'}
              </h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            
            <form onSubmit={handleSubmit} className="form">
              {/* Personal Information */}
              <h3 style={{ marginBottom: '1rem', borderBottom: '2px solid #e0e0e0', paddingBottom: '0.5rem' }}>
                Personal Information
              </h3>
              
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">First Name <span style={{ color: 'red' }}>*</span></label>
                  <input
                    type="text"
                    className={`form-input ${errors.firstName ? 'error' : ''}`}
                    value={formData.firstName || ''}
                    onChange={(e) => {
                      setFormData({...formData, firstName: e.target.value});
                      if (errors.firstName) setErrors({...errors, firstName: ''});
                    }}
                    placeholder="Enter first name"
                  />
                  {errors.firstName && <span className="error-text">{errors.firstName}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">Last Name <span style={{ color: 'red' }}>*</span></label>
                  <input
                    type="text"
                    className={`form-input ${errors.lastName ? 'error' : ''}`}
                    value={formData.lastName || ''}
                    onChange={(e) => {
                      setFormData({...formData, lastName: e.target.value});
                      if (errors.lastName) setErrors({...errors, lastName: ''});
                    }}
                    placeholder="Enter last name"
                  />
                  {errors.lastName && <span className="error-text">{errors.lastName}</span>}
                </div>
              </div>
              
              {/* Contact Information */}
              <h3 style={{ marginTop: '1.5rem', marginBottom: '1rem', borderBottom: '2px solid #e0e0e0', paddingBottom: '0.5rem' }}>
                Contact Information
              </h3>
              
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Email <span style={{ color: 'red' }}>*</span></label>
                  <input
                    type="email"
                    className={`form-input ${errors.email ? 'error' : ''}`}
                    value={formData.email || ''}
                    onChange={(e) => {
                      setFormData({...formData, email: e.target.value});
                      if (errors.email) setErrors({...errors, email: ''});
                    }}
                    placeholder="example@email.com"
                  />
                  {errors.email && <span className="error-text">{errors.email}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">Phone <span style={{ color: 'red' }}>*</span></label>
                  <input
                    type="tel"
                    className={`form-input ${errors.phone ? 'error' : ''}`}
                    value={formData.phone || ''}
                    onChange={(e) => {
                      setFormData({...formData, phone: e.target.value});
                      if (errors.phone) setErrors({...errors, phone: ''});
                    }}
                    placeholder="1234567890"
                  />
                  {errors.phone && <span className="error-text">{errors.phone}</span>}
                </div>
              </div>
              
              <div className="form-group">
                <label className="form-label">
                  <input
                    type="checkbox"
                    checked={formData.isWhatsappNumber || false}
                    onChange={(e) => setFormData({...formData, isWhatsappNumber: e.target.checked})}
                    style={{ marginRight: '0.5rem' }}
                  />
                  This is a WhatsApp number
                </label>
              </div>

              <div className="form-group">
                <label className="form-label">Address</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.address || ''}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                  placeholder="Enter address"
                />
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">City <span style={{ color: 'red' }}>*</span></label>
                  <input
                    type="text"
                    className={`form-input ${errors.city ? 'error' : ''}`}
                    value={formData.city || ''}
                    onChange={(e) => {
                      setFormData({...formData, city: e.target.value});
                      if (errors.city) setErrors({...errors, city: ''});
                    }}
                    placeholder="Enter city"
                  />
                  {errors.city && <span className="error-text">{errors.city}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">Gender <span style={{ color: 'red' }}>*</span></label>
                  <select
                    className={`form-input ${errors.gender ? 'error' : ''}`}
                    value={formData.gender || ''}
                    onChange={(e) => {
                      setFormData({...formData, gender: e.target.value});
                      if (errors.gender) setErrors({...errors, gender: ''});
                    }}
                  >
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                  {errors.gender && <span className="error-text">{errors.gender}</span>}
                </div>
              </div>

              {/* Additional Information */}
              <h3 style={{ marginTop: '1.5rem', marginBottom: '1rem', borderBottom: '2px solid #e0e0e0', paddingBottom: '0.5rem' }}>
                Additional Information
              </h3>

              <div className="form-row">
              <div className="form-group">
                <label className="form-label">Date of Birth <span style={{ color: 'red' }}>*</span></label>
                <input
                  type="date"
                  className={`form-input ${errors.dateOfBirth ? 'error' : ''}`}
                  value={formData.dateOfBirth ? new Date(formData.dateOfBirth).toISOString().split('T')[0] : ''}
                  onChange={(e) => {
                    setFormData({...formData, dateOfBirth: e.target.value});
                    if (errors.dateOfBirth) setErrors({...errors, dateOfBirth: ''});
                  }}
                  max={new Date().toISOString().split('T')[0]}
                />
                {errors.dateOfBirth && <span className="error-text">{errors.dateOfBirth}</span>}
              </div>
                <div className="form-group">
                  <label className="form-label">Occupation</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.occupation || ''}
                    onChange={(e) => setFormData({...formData, occupation: e.target.value})}
                    placeholder="Enter occupation"
                  />
                </div>
              </div>

              {/* Fitness Interest */}
              <div className="form-group">
                <label className="form-label">Interest/Focus Area</label>
                <select
                  className="form-input"
                  value={formData.interestType || ''}
                  onChange={(e) => setFormData({...formData, interestType: e.target.value})}
                  style={{ fontSize: '14px' }}
                >
                  <option value="">Select Interest</option>
                  <option value="Cardio">🏃 Cardio</option>
                  <option value="Strength Training">💪 Strength Training</option>
                  <option value="Weight Loss">⚖️ Weight Loss</option>
                  <option value="Muscle Gain">🏋️ Muscle Gain</option>
                  <option value="General Fitness">🎯 General Fitness</option>
                  <option value="Yoga">🧘 Yoga</option>
                  <option value="CrossFit">⚡ CrossFit</option>
                  <option value="Both Cardio & Strength">🔥 Both Cardio & Strength</option>
                  <option value="Sports Training">⚽ Sports Training</option>
                  <option value="Other">📝 Other</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Remark/Notes</label>
                <textarea
                  className="form-input"
                  value={formData.remark || ''}
                  onChange={(e) => setFormData({...formData, remark: e.target.value})}
                  placeholder="Any additional notes or special requirements..."
                  rows={4}
                  style={{ resize: 'vertical', fontFamily: 'inherit' }}
                />
                <small style={{ color: '#666', fontSize: '12px' }}>
                  Maximum 1000 characters
                </small>
              </div>
              
              <div className="form-actions" style={{ marginTop: '2rem' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {formData.enquiryId ? 'Update Enquiry' : 'Create Enquiry'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Convert to Member Modal */}
      {showConvertModal && selectedEnquiry && (
        <ConvertMemberModal
          enquiry={selectedEnquiry}
          plans={membershipPlans}
          onConvert={handleConvertToMember}
          onClose={() => setShowConvertModal(false)}
        />
      )}

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="modal-overlay" onClick={() => setShowConfirmDialog(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2 className="modal-title">Confirm Action</h2>
              <button className="modal-close" onClick={() => setShowConfirmDialog(false)}>×</button>
            </div>
            
            <div style={{ padding: '1.5rem' }}>
              <p style={{ margin: 0, fontSize: '1rem', lineHeight: '1.5' }}>{confirmDialogMessage}</p>
            </div>
            
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowConfirmDialog(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleConfirmAction}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Convert Member Modal Component
interface ConvertMemberModalProps {
  enquiry: Enquiry;
  plans: any[];
  onConvert: (
    planId: number, 
    amount: number,
    discount: number,
    startDate: string,
    paymentMode: string,
    transactionId: string,
    expectedPaymentDate: string
  ) => void;
  onClose: () => void;
}

const ConvertMemberModal: React.FC<ConvertMemberModalProps> = ({
  enquiry,
  plans,
  onConvert,
  onClose
}) => {
  const [selectedPlan, setSelectedPlan] = useState<number | null>(null);
  const [paidAmount, setPaidAmount] = useState<string>('');
  const [discount, setDiscount] = useState<string>('0');
  const [startDate, setStartDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [paymentMode, setPaymentMode] = useState<string>('');
  const [transactionId, setTransactionId] = useState<string>('');
  const [expectedPaymentDate, setExpectedPaymentDate] = useState<string>('');
  const [errors, setErrors] = useState<{[key: string]: string}>({});

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const newErrors: {[key: string]: string} = {};
    
    if (!selectedPlan) {
      newErrors.selectedPlan = 'Please select a membership plan';
    }
    
    if (!paidAmount || parseFloat(paidAmount) < 0) {
      newErrors.paidAmount = 'Please enter a valid payment amount';
    }
    
    if (!startDate) {
      newErrors.startDate = 'Please select a start date';
    }
    
    if (!paymentMode) {
      newErrors.paymentMode = 'Please select a payment mode';
    }
    
    setErrors(newErrors);
    
    if (Object.keys(newErrors).length === 0 && selectedPlan && paidAmount && paymentMode) {
      onConvert(
        selectedPlan, 
        parseFloat(paidAmount),
        parseFloat(discount || '0'),
        startDate,
        paymentMode,
        transactionId,
        expectedPaymentDate
      );
    }
  };

  const selectedPlanData = plans.find(p => p.membershipPlanId === selectedPlan);
  
  // Calculate total after discount
  const totalAfterDiscount = selectedPlanData 
    ? selectedPlanData.price - parseFloat(discount || '0')
    : 0;
  
  // Calculate remaining amount after discount and payment
  const remainingAmount = totalAfterDiscount - parseFloat(paidAmount || '0');
  
  const calculateEndDate = () => {
    if (!startDate || !selectedPlanData) return '';
    const start = new Date(startDate);
    start.setMonth(start.getMonth() + selectedPlanData.durationInMonths);
    return start.toLocaleDateString();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">Convert to Member</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        
        <form onSubmit={handleSubmit} className="form">
          <div style={{ marginBottom: '1rem', padding: '1rem', background: '#f8f9fa', borderRadius: '8px' }}>
            <h3 style={{ marginBottom: '0.5rem' }}>
              {enquiry.firstName} {enquiry.lastName}
            </h3>
            <p style={{ margin: 0, color: '#666' }}>
              {enquiry.email} | {enquiry.phone}
              {enquiry.interestType && (
                <> | Interest: <strong>{enquiry.interestType}</strong></>
              )}
            </p>
          </div>

          {/* Membership Plan Selection */}
          <div className="form-group">
            <label className="form-label">Select Membership Plan <span style={{ color: 'red' }}>*</span></label>
            <select
              className={`form-input ${errors.selectedPlan ? 'error' : ''}`}
              value={selectedPlan || ''}
              onChange={(e) => {
                setSelectedPlan(Number(e.target.value));
                if (errors.selectedPlan) setErrors({...errors, selectedPlan: ''});
              }}
              required
              style={{ fontSize: '0.95rem' }}
            >
              <option value="">-- Select a Membership Plan --</option>
              {plans.length === 0 ? (
                <option value="" disabled>No active plans available</option>
              ) : (
                plans.map(plan => (
                  <option key={plan.membershipPlanId} value={plan.membershipPlanId}>
                    {plan.planName} - ₹{plan.price} ({plan.durationInMonths} months)
                  </option>
                ))
              )}
            </select>
            {errors.selectedPlan && <span className="error-text">{errors.selectedPlan}</span>}
          </div>

          {/* Plan Details */}
          {selectedPlanData && (
            <div style={{ padding: '1rem', background: '#DBEAFE', borderRadius: '8px', marginBottom: '1rem', border: '1px solid #3B82F6' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', fontSize: '0.9rem' }}>
                <div>
                  <strong style={{ color: '#1E40AF' }}>Plan:</strong>
                  <div style={{ color: '#374151' }}>{selectedPlanData.planName}</div>
                </div>
                <div>
                  <strong style={{ color: '#1E40AF' }}>Total Amount:</strong>
                  <div style={{ color: '#374151', fontSize: '1.1rem', fontWeight: '600' }}>₹{selectedPlanData.price}</div>
                </div>
                <div>
                  <strong style={{ color: '#1E40AF' }}>Duration:</strong>
                  <div style={{ color: '#374151' }}>{selectedPlanData.durationInMonths} months</div>
                </div>
                {startDate && (
                  <div>
                    <strong style={{ color: '#1E40AF' }}>End Date:</strong>
                    <div style={{ color: '#374151' }}>{calculateEndDate()}</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Start Date */}
          <div className="form-group">
            <label className="form-label">Membership Start Date <span style={{ color: 'red' }}>*</span></label>
            <input
              type="date"
              className={`form-input ${errors.startDate ? 'error' : ''}`}
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                if (errors.startDate) setErrors({...errors, startDate: ''});
              }}
              required
            />
            {errors.startDate && <span className="error-text">{errors.startDate}</span>}
            <small style={{ color: '#666', fontSize: '12px' }}>
              Select when the membership should start (defaults to today)
            </small>
          </div>

          {/* Initial Payment */}
          <div className="form-group">
            <label className="form-label">Initial Payment Amount <span style={{ color: 'red' }}>*</span></label>
            <input
              type="number"
              className={`form-input ${errors.paidAmount ? 'error' : ''}`}
              value={paidAmount}
              onChange={(e) => {
                setPaidAmount(e.target.value);
                if (errors.paidAmount) setErrors({...errors, paidAmount: ''});
              }}
              min="0"
              step="0.01"
              max={selectedPlanData?.price || undefined}
              placeholder="0.00"
              required
            />
            {errors.paidAmount && <span className="error-text">{errors.paidAmount}</span>}
            <small style={{ color: '#666', fontSize: '12px' }}>
              Enter the amount being paid now. Can be partial payment (minimum ₹0, maximum: ₹{selectedPlanData?.price || 0})
            </small>
          </div>

          {/* Discount */}
          <div className="form-group">
            <label className="form-label">Discount Amount</label>
            <input
              type="number"
              className="form-input"
              value={discount}
              onChange={(e) => setDiscount(e.target.value)}
              min="0"
              step="0.01"
              max={selectedPlanData?.price || undefined}
              placeholder="0.00"
            />
            <small style={{ color: '#666', fontSize: '12px' }}>
              💰 Enter discount amount if applicable (e.g., promotional discount, referral bonus, etc.)
            </small>
          </div>

          {/* Payment Mode */}
          <div className="form-group">
            <label className="form-label">Payment Mode <span style={{ color: 'red' }}>*</span></label>
            <select
              className={`form-input ${errors.paymentMode ? 'error' : ''}`}
              value={paymentMode}
              onChange={(e) => {
                setPaymentMode(e.target.value);
                if (errors.paymentMode) setErrors({...errors, paymentMode: ''});
              }}
              required
            >
              <option value="">-- Select Payment Mode --</option>
              <option value="Cash">💵 Cash</option>
              <option value="UPI">📱 UPI</option>
              <option value="Credit Card">💳 Credit Card</option>
              <option value="Debit Card">💳 Debit Card</option>
              <option value="Net Banking">🏦 Net Banking</option>
              <option value="Cheque">📝 Cheque</option>
              <option value="Bank Transfer">🏦 Bank Transfer</option>
            </select>
            {errors.paymentMode && <span className="error-text">{errors.paymentMode}</span>}
          </div>

          {/* Transaction ID */}
          <div className="form-group">
            <label className="form-label">Transaction ID / Reference Number</label>
            <input
              type="text"
              className="form-input"
              value={transactionId}
              onChange={(e) => setTransactionId(e.target.value)}
              placeholder="Enter transaction ID or reference number"
            />
            <small style={{ color: '#666', fontSize: '12px' }}>
              Optional - Enter transaction ID for digital payments or cheque number
            </small>
          </div>

          {/* Remaining Amount Display */}
          {selectedPlanData && paidAmount && parseFloat(paidAmount) >= 0 && (
            <div style={{ 
              padding: '0.75rem', 
              background: remainingAmount > 0 ? '#FEF3C7' : '#D1FAE5', 
              borderRadius: '8px', 
              marginBottom: '1rem',
              border: `1px solid ${remainingAmount > 0 ? '#F59E0B' : '#10B981'}`
            }}>
              {parseFloat(discount || '0') > 0 && (
                <div style={{ marginBottom: '0.5rem', fontSize: '0.875rem', color: '#059669' }}>
                  <strong>Plan Price:</strong> ₹{selectedPlanData.price.toFixed(2)} | 
                  <strong> Discount:</strong> -₹{parseFloat(discount || '0').toFixed(2)} | 
                  <strong> Total:</strong> ₹{totalAfterDiscount.toFixed(2)}
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontWeight: '500', color: '#374151' }}>Remaining Balance:</span>
                <span style={{ 
                  fontSize: '1.25rem', 
                  fontWeight: 'bold', 
                  color: remainingAmount > 0 ? '#F59E0B' : '#10B981'
                }}>
                  ₹{remainingAmount > 0 ? remainingAmount.toFixed(2) : '0.00'}
                </span>
              </div>
              {remainingAmount > 0 && (
                <small style={{ color: '#92400E', fontSize: '0.75rem' }}>
                  ⚠️ This is a partial payment. Remaining amount can be collected later.
                </small>
              )}
            </div>
          )}

          {/* Expected Payment Date - Only show if there's remaining balance */}
          {selectedPlanData && paidAmount && remainingAmount > 0 && parseFloat(paidAmount) >= 0 && (
            <div className="form-group">
              <label className="form-label">Next Payment Expected Date</label>
              <input
                type="date"
                className="form-input"
                value={expectedPaymentDate}
                onChange={(e) => setExpectedPaymentDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
              />
              <small style={{ color: '#666', fontSize: '12px' }}>
                📅 When do you expect the member to pay the remaining ${remainingAmount.toFixed(2)}? (Optional - defaults to 1 month if not set)
              </small>
            </div>
          )}

          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-success">
              Convert to Member
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Enquiries;

